import React from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import useOnclickOutside from 'react-cool-onclickoutside';
import styles from './style.module.scss';
import '../../assets/icons/countryFlags/flags.css';
import SEFlagIcon from '@assets/icons/countryFlags/se.svg';
import USAFlagIcon from '@assets/icons/countryFlags/USAFlag.svg';
import ESFlagIcon from '@assets/icons/countryFlags/es.svg';
import FRFlagIcon from '@assets/icons/countryFlags/fr.svg';
import DEFlagIcon from '@assets/icons/countryFlags/de.svg';
import ITFlagIcon from '@assets/icons/countryFlags/it.svg';
import PTFlagIcon from '@assets/icons/countryFlags/pt.svg';
import RUFlagIcon from '@assets/icons/countryFlags/ru.svg';
import JPFlagIcon from '@assets/icons/countryFlags/jp.svg';
import { pricingFx } from '../../features/pricing/model';
import { $userLanguage, $user, updateUserLangFx, userLanaguageEvent, SupportedLanguage } from '../../features/app/model';
import { useLocation, useNavigate } from 'react-router-dom';

interface LanguageInfoProps {
  position: React.CSSProperties;
  set: (value: boolean) => void;
  val: boolean;
}

const LANGUAGES: Array<{ code: SupportedLanguage; flag: string; name: string }> = [
    { code: 'EN', flag: USAFlagIcon, name: 'English' },
    { code: 'SV', flag: SEFlagIcon, name: 'Svenska' },
    { code: 'ES', flag: ESFlagIcon, name: 'Español' },
    { code: 'FR', flag: FRFlagIcon, name: 'Français' },
    { code: 'DE', flag: DEFlagIcon, name: 'Deutsch' },
    { code: 'IT', flag: ITFlagIcon, name: 'Italiano' },
    { code: 'PT', flag: PTFlagIcon, name: 'Português' },
    { code: 'RU', flag: RUFlagIcon, name: 'Русский' },
    { code: 'JA', flag: JPFlagIcon, name: '日本語' }
];

export const LanguageInfo: React.FC<LanguageInfoProps> = ({ position, set, val }) => {
    const currentLang = useUnit($userLanguage);
    const [user, update] = useUnit([$user, updateUserLangFx]);
    const requestSubs = useUnit(pricingFx);
    const { i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const ref = useOnclickOutside(() => {
        set(false);
    });

    // Function to determine the current language from URL or storage
    const getCurrentLanguage = (): SupportedLanguage => {
        // First, check URL for language
        const pathSegments = location.pathname.split('/').filter(Boolean);
        const currentLangSegment = pathSegments[0]?.toLowerCase();
        const supportedLangs = LANGUAGES.map(l => l.code.toLowerCase());
        
        if (supportedLangs.includes(currentLangSegment)) {
            return currentLangSegment.toUpperCase() as SupportedLanguage;
        }
        
        // Fallback to stored language
        const storedLang = localStorage.getItem('i18nextLng');
        if (storedLang && supportedLangs.includes(storedLang)) {
            return storedLang.toUpperCase() as SupportedLanguage;
        }
        
        return 'EN';
    };

    // Set initial language on component mount
    React.useEffect(() => {
        const initialLang = getCurrentLanguage();
        const lowerLang = initialLang.toLowerCase();
        
        // Only update i18n if it's different from current
        if (i18n.language !== lowerLang) {
            i18n.changeLanguage(lowerLang).catch(err => {
                console.error('Error loading language:', err);
            });
        }
        
        // Update internal state
        userLanaguageEvent(initialLang);
    }, [location.pathname]);

    const updateUrlLanguage = (lang: SupportedLanguage) => {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        const currentLangSegment = pathSegments[0]?.toLowerCase();
        const supportedLangs = LANGUAGES.map(l => l.code.toLowerCase());
        const langLower = lang.toLowerCase();
        
        // Pages that should NOT have language routes (English only)
        const englishOnlyPages = [
            '/bypass-ai-detection',
            '/gptzero-alternative', 
            '/paraphrasing-tool',
            '/ai-detector',
            '/shadowgpt',
            '/pricing',
            '/auth'
        ];
        
        // Check if current path starts with a language code
        const startsWithLangCode = supportedLangs.includes(currentLangSegment);
        
        // Get the actual path without language prefix
        const actualPath = startsWithLangCode ? '/' + pathSegments.slice(1).join('/') : location.pathname;
        
        // If current page is English-only, don't add language prefix - just stay on the same page
        if (englishOnlyPages.includes(actualPath)) {
            // Don't navigate anywhere, but language change will still work through i18next
            return;
        }
        
        if (location.pathname === '/') {
            // Main page without language -> add language
            navigate('/' + langLower);
        } else if (startsWithLangCode) {
            // Path already has language prefix -> replace it
            const pathWithoutLang = '/' + pathSegments.slice(1).join('/');
            
            // Special handling for summarizer
            if (pathWithoutLang === '/summarizer' || pathWithoutLang === '/' || pathWithoutLang === '/paragraph-generator' || pathWithoutLang === '/text-message-generator' || pathWithoutLang === '/abstract-generator') {
                navigate('/' + langLower + pathWithoutLang);
            } else {
                // For other pages with language prefix, stay on English version
                navigate(pathWithoutLang);
            }
        } else {
            // Path without language prefix -> only add for main page and summarizer
            if (location.pathname === '/summarizer' || location.pathname === '/paragraph-generator' || location.pathname === '/text-message-generator' || location.pathname === '/abstract-generator') {
                navigate('/' + langLower + location.pathname);
            }
            // For other pages, don't add language prefix
        }
    };

    const changeLanguage = (lang: SupportedLanguage) => {
        userLanaguageEvent(lang);
        
        // Convert to lowercase for i18next
        const lowerLang = lang.toLowerCase();
        
        i18n.changeLanguage(lowerLang)
            .catch(err => {
                console.error('Error loading language:', err);
            });
        
        // Update URL for all pages
        updateUrlLanguage(lang);
        
        const currencyCode = lang === 'SV' ? 'SEK' : 'USD';
        if (Object.keys(user).length) {
            update({
                languageCode: lang,
                currencyCode: currencyCode,
            }).then(() => {
                requestSubs({
                    currencyCode: currencyCode,
                    languageCode: lang,
                });
            });
        } else {
            requestSubs({
                currencyCode: currencyCode,
                languageCode: lang,
            });
        }
        
        // Store lowercase version in localStorage to match i18next expectations
        localStorage.setItem('i18nextLng', lowerLang);
        set(false);
    };

    const currentLanguage = getCurrentLanguage();
    const currentLangData = LANGUAGES.find(l => l.code === currentLanguage) || LANGUAGES[0];

    return (
        <div className={styles.LanguageInfo} ref={ref}>
            <div
                className={styles.LanguageInfo__header}
                onClick={() => set(!val)}
            >
                <div className="flag-icon">
                    <img src={currentLangData.flag} alt={currentLangData.name} />
                </div>
                <p className={styles.LanguageInfo__language}>{currentLangData.code}</p>
            </div>
            <div
                style={position}
                className={
                    val
                        ? `${styles.LanguageInfoBody__show} ${styles.LanguageInfoBody}`
                        : styles.LanguageInfoBody
                }
            >
                {LANGUAGES.map((lang) => (
                    <div
                        key={lang.code}
                        className={styles.LanguageInfoBody__row}
                        onClick={() => changeLanguage(lang.code)}
                    >
                        <div className="flag-icon">
                            <img src={lang.flag} alt={lang.name} />
                        </div>
                        <p className={styles.LanguageInfo__language}>{lang.code}</p>
                    </div>
                ))}
            </div>
        </div>
    );
};